import * as layout from 'echarts/lib/util/layout';
import * as zrUtil from 'zrender/src/core/util';
import { groupData } from 'echarts/lib/util/model';
import ExtensionAPI from 'echarts/lib/core/ExtensionAPI';
import SankeySeriesModel, {
  SankeySeriesOption,
  SankeyNodeItemOption
} from 'echarts/lib/chart/sankey/sankeySeries';
import { GraphNode, GraphEdge } from 'echarts/lib/data/Graph';
import { LayoutOrient } from 'echarts/lib/util/types';
import GlobalModel from 'echarts/lib/model/Global';

export default function sankeyTreeLayout(ecModel: GlobalModel, api: ExtensionAPI) {
  ecModel.eachSeriesByType('sankey', function (seriesModel: SankeySeriesModel) {
    if (!seriesModel.get('dvLayout') && seriesModel.get('dvLayout') !== 'tree') {
      return;
    }
    const nodeWidth = seriesModel.get('nodeWidth');
    const nodeGap = seriesModel.get('nodeGap');
    const rootHeight = seriesModel.get('dvRootHeight') || 0;
    const rootWidthPercent = seriesModel.get('dvNodeLengthPercent') || 0.66;
    const layoutInfo = getViewRect(seriesModel, api);

    seriesModel.layoutInfo = layoutInfo;
    const width = layoutInfo.width;
    const height = layoutInfo.height;

    const graph = seriesModel.getGraph();

    const nodes = graph.nodes;
    const edges = graph.edges;

    computeNodeValues(nodes);

    const filteredNodes = zrUtil.filter(nodes, function (node) {
      return node.getLayout().value === 0;
    });

    const orient = seriesModel.get('orient');

    const nodeAlign = seriesModel.get('nodeAlign');

    layoutSankey(
      nodes,
      edges,
      nodeWidth,
      nodeGap,
      width,
      height,
      orient,
      nodeAlign,
      rootHeight,
      rootWidthPercent
    );
  });
}

/**
 * Get the layout position of the whole view
 */
function getViewRect(seriesModel: SankeySeriesModel, api: ExtensionAPI) {
  return layout.getLayoutRect(seriesModel.getBoxLayoutParams(), {
    width: api.getWidth(),
    height: api.getHeight()
  });
}

function layoutSankey(
  nodes: GraphNode[],
  edges: GraphEdge[],
  nodeWidth: number,
  nodeGap: number,
  width: number,
  height: number,
  orient: LayoutOrient,
  nodeAlign: SankeySeriesOption['nodeAlign'],
  rootHeight: number,
  rootWidthPercent: number
) {
  computeNodeBreadths(nodes, edges, nodeWidth, width, height, orient, nodeAlign, rootHeight);
  computeNodeDepths(nodes, edges, height, width, nodeGap, orient, rootWidthPercent);
  computeEdgeDepths(nodes, orient);
}
/**
 *
 * @param node
 * 对node做处理，改变其outEdge和inEdge
 */
function handleNode(node) {
  let inEdgesSameLayerTop = [];
  let inEdgesSameLayerBottom = [];
  let outEdgesSameLayerTop = [];
  let outEdgesSameLayerBottom = [];
  let edges = {
    inEdges: [],
    outEdges: [],
    id: node.id
  };

  // 将node.inEdges里的同层连接的边过滤出来，同时区分方向
  node.inEdges.forEach(item => {
    if (item.getModel().option.dvDirection) {
      if (item.getModel().option.dvDirection === 'top') {
        inEdgesSameLayerTop.push(item);
      } else {
        inEdgesSameLayerBottom.push(item);
      }
    }
  });
  // 将node.outEdges里的同层links过滤出来，同时区分方向
  node.outEdges.forEach(item => {
    if (item.getModel().option.dvDirection) {
      if (item.getModel().option.dvDirection === 'top') {
        outEdgesSameLayerTop.push(item);
      } else {
        outEdgesSameLayerBottom.push(item);
      }
    }
  });
  // 将node.inEdges里的同层连接的边过滤掉
  const nodeInEdgesOthers = node.inEdges.filter(item => !item.getModel().option.dvDirection);
  // 将node.outEdges里的同层连接的边过滤掉
  const nodeOutEdgesOthers = node.outEdges.filter(item => !item.getModel().option.dvDirection);
  if (
    inEdgesSameLayerTop.length != 0 ||
    outEdgesSameLayerTop.length != 0 ||
    inEdgesSameLayerBottom.length != 0 ||
    outEdgesSameLayerBottom.length != 0
  ) {
    // 将过滤出的同层links方向为top的添加进edges.inEdges
    edges.inEdges = [
      ...edges.inEdges,
      ...nodeInEdgesOthers,
      ...inEdgesSameLayerTop,
      ...outEdgesSameLayerTop
    ];
    // 将过滤出的同层links方向为bottom的添加进edges.outEdges
    edges.outEdges = [
      ...edges.outEdges,
      ...nodeOutEdgesOthers,
      ...inEdgesSameLayerBottom,
      ...outEdgesSameLayerBottom
    ];
  }
  if (edges.inEdges.length === 0 && edges.outEdges.length === 0) {
    return false;
  } else {
    return edges;
  }
}
/**
 * Compute the value of each node by summing the associated edge's value
 */
function computeNodeValues(nodes: GraphNode[]) {
  zrUtil.each(nodes, function (node) {
    // 当连线在节点上方（根据dvdirection判断），且连线是从节点下方出去的（也就是outEdge）
    const newNode = handleNode(node);

    const value1 = sum(newNode ? newNode.outEdges : node.outEdges, getEdgeValue);
    // 节点入边总宽度
    const value2 = sum(newNode ? newNode.inEdges : node.inEdges, getEdgeValue);
    const nodeRawValue = (node.getValue() as number) || 0;
    const value = Math.max(value1, value2, nodeRawValue);
    node.setLayout({ value: value }, true);
  });
}

/**
 * Compute the x-position for each node.
 *
 * Here we use Kahn algorithm to detect cycle when we traverse
 * the node to computer the initial x position.
 */
function computeNodeBreadths(
  nodes: GraphNode[],
  edges: GraphEdge[],
  nodeWidth: number,
  width: number,
  height: number,
  orient: LayoutOrient,
  nodeAlign: SankeySeriesOption['nodeAlign'],
  rootHeight: number
) {
  // Used to mark whether the edge is deleted. if it is deleted,
  // the value is 0, otherwise it is 1.
  const remainEdges = [];
  // Storage each node's indegree.
  const indegreeArr = [];
  // Used to storage the node with indegree is equal to 0.
  let zeroIndegrees: GraphNode[] = [];
  let nextTargetNode: GraphNode[] = [];
  let x = 0;
  // let kx = 0;

  for (let i = 0; i < edges.length; i++) {
    remainEdges[i] = 1;
  }
  for (let i = 0; i < nodes.length; i++) {
    indegreeArr[i] = nodes[i].inEdges.length;
    if (indegreeArr[i] === 0) {
      zeroIndegrees.push(nodes[i]);
    }
  }
  let maxNodeDepth = -1;
  // Traversing nodes using topological sorting to calculate the
  // horizontal(if orient === 'horizontal') or vertical(if orient === 'vertical')
  // position of the nodes.
  while (zeroIndegrees.length) {
    for (let idx = 0; idx < zeroIndegrees.length; idx++) {
      const node = zeroIndegrees[idx];
      const item = node.hostGraph.data.getRawDataItem(node.dataIndex) as SankeyNodeItemOption;
      const isItemDepth = item.depth != null && item.depth >= 0;
      if (isItemDepth && item.depth > maxNodeDepth) {
        maxNodeDepth = item.depth;
      }
      node.setLayout({ depth: isItemDepth ? item.depth : x }, true);
      node.getModel().option.root === false
        ? orient === 'vertical'
          ? node.setLayout({ dy: nodeWidth }, true)
          : node.setLayout({ dx: nodeWidth }, true)
        : orient === 'vertical'
        ? node.setLayout({ dy: rootHeight }, true)
        : node.setLayout({ dx: rootHeight }, true);

      for (let edgeIdx = 0; edgeIdx < node.outEdges.length; edgeIdx++) {
        const edge = node.outEdges[edgeIdx];
        const indexEdge = edges.indexOf(edge);
        remainEdges[indexEdge] = 0;
        const targetNode = edge.node2;
        const nodeIndex = nodes.indexOf(targetNode);
        if (--indegreeArr[nodeIndex] === 0 && nextTargetNode.indexOf(targetNode) < 0) {
          nextTargetNode.push(targetNode);
        }
      }
    }
    ++x;
    zeroIndegrees = nextTargetNode;
    nextTargetNode = [];
  }

  for (let i = 0; i < remainEdges.length; i++) {
    if (remainEdges[i] === 1) {
      throw new Error('Sankey is a DAG, the original data has cycle!');
    }
  }

  // const maxDepth = maxNodeDepth > x - 1 ? maxNodeDepth : x - 1;
  // 按照最大层级处理间隔
  const maxDepth = maxNodeDepth;
  if (nodeAlign && nodeAlign !== 'left') {
    adjustNodeWithNodeAlign(nodes, nodeAlign, orient, maxDepth);
  }
  const kx =
    orient === 'vertical' ? (height - rootHeight) / maxDepth : (width - rootHeight) / maxDepth;
  scaleNodeBreadths(nodes, kx, orient, rootHeight, nodeWidth);
}

function isNodeDepth(node: GraphNode) {
  const item = node.hostGraph.data.getRawDataItem(node.dataIndex) as SankeyNodeItemOption;
  return item.depth != null && item.depth >= 0;
}

function adjustNodeWithNodeAlign(
  nodes: GraphNode[],
  nodeAlign: SankeySeriesOption['nodeAlign'],
  orient: LayoutOrient,
  maxDepth: number
) {
  if (nodeAlign === 'right') {
    let nextSourceNode: GraphNode[] = [];
    let remainNodes = nodes;
    let nodeHeight = 0;
    while (remainNodes.length) {
      for (let i = 0; i < remainNodes.length; i++) {
        const node = remainNodes[i];
        node.setLayout({ skNodeHeight: nodeHeight }, true);
        for (let j = 0; j < node.inEdges.length; j++) {
          const edge = node.inEdges[j];
          if (nextSourceNode.indexOf(edge.node1) < 0) {
            nextSourceNode.push(edge.node1);
          }
        }
      }
      remainNodes = nextSourceNode;
      nextSourceNode = [];
      ++nodeHeight;
    }

    zrUtil.each(nodes, function (node) {
      if (!isNodeDepth(node)) {
        node.setLayout({ depth: Math.max(0, maxDepth - node.getLayout().skNodeHeight) }, true);
      }
    });
  } else if (nodeAlign === 'justify') {
    moveSinksRight(nodes, maxDepth);
  }
}

/**
 * All the node without outEgdes are assigned maximum x-position and
 *     be aligned in the last column.
 *
 * @param nodes.  node of sankey view.
 * @param maxDepth.  use to assign to node without outEdges as x-position.
 */
function moveSinksRight(nodes: GraphNode[], maxDepth: number) {
  zrUtil.each(nodes, function (node) {
    const newNode = handleNode(node);
    if (!isNodeDepth(node) && !(newNode ? newNode.outEdges.length : node.outEdges.length)) {
      node.setLayout({ depth: maxDepth }, true);
    }
  });
}

/**
 * Scale node x-position to the width
 *
 * @param nodes  node of sankey view
 * @param kx   multiple used to scale nodes
 */
function scaleNodeBreadths(
  nodes: GraphNode[],
  kx: number,
  orient: LayoutOrient,
  rootHeight: number,
  nodeWidth: number
) {
  let dis = 0;
  zrUtil.each(nodes, function (node) {
    const nodeDepth = node.getLayout().depth * kx + dis;
    node.getModel().option.root === true && (dis = rootHeight - nodeWidth);
    orient === 'vertical'
      ? node.setLayout({ y: nodeDepth }, true)
      : node.setLayout({ x: nodeDepth }, true);
  });
}

/**
 * compute the node depth(y-position)
 *
 * @param nodes  node of sankey view
 * @param edges  edge of sankey view
 * @param height  the whole height of the area to draw the view
 * @param nodeGap  the vertical distance between two nodes
 */
function computeNodeDepths(
  nodes: GraphNode[],
  edges: GraphEdge[],
  height: number,
  width: number,
  nodeGap: number,
  orient: LayoutOrient,
  rootWidthPercent: number
) {
  const nodesByBreadth = prepareNodesByBreadth(nodes, orient);

  initializeNodeDepth(nodesByBreadth, edges, height, width, nodeGap, orient, rootWidthPercent);
  resolveCollisions(nodesByBreadth, nodeGap, height, width, orient);

  const nodeDepthsWidthArray = [];
  const directionKey = orient === 'vertical' ? width : height;
  const positionKey = orient === 'vertical' ? 'x' : 'y';
  for (var i = 0; i < nodesByBreadth.length; i++) {
    const nodeDepthsWidth = nodesByBreadth[i].reduce((a, b) => a + b.getLayout().value, 0);
    nodeDepthsWidthArray.push(nodeDepthsWidth);
  }
  for (var i = 0; i < nodesByBreadth.length; i++) {
    const nodeDepthsWidth = nodesByBreadth[i].reduce((a, b) => a + b.getLayout().value, 0);
    const rootWidth = rootWidthPercent * directionKey;
    const eachNodeDistance =
      (directionKey - (rootWidth * nodeDepthsWidth) / Math.max(...nodeDepthsWidthArray)) /
      (nodesByBreadth[i].length + 1);
    nodesByBreadth[i].map((item, i) => {
      item.getLayout()[positionKey] = item.getLayout()[positionKey] + eachNodeDistance * (i + 1);
    });
  }
}

function prepareNodesByBreadth(nodes: GraphNode[], orient: LayoutOrient) {
  const nodesByBreadth: GraphNode[][] = [];
  const keyAttr = orient === 'vertical' ? 'y' : 'x';

  const groupResult = groupData(nodes, function (node) {
    return node.getLayout()[keyAttr] as number;
  });
  groupResult.keys.sort(function (a, b) {
    return a - b;
  });
  zrUtil.each(groupResult.keys, function (key) {
    nodesByBreadth.push(groupResult.buckets.get(key));
  });

  return nodesByBreadth;
}

/**
 * Compute the original y-position for each node
 */
function initializeNodeDepth(
  nodesByBreadth: GraphNode[][],
  edges: GraphEdge[],
  height: number,
  width: number,
  nodeGap: number,
  orient: LayoutOrient,
  rootWidthPercent: number
) {
  let minKy = Infinity;
  zrUtil.each(nodesByBreadth, function (nodes) {
    const n = nodes.length;
    let sum = 0;
    zrUtil.each(nodes, function (node) {
      sum += node.getLayout().value;
    });
    const nodeWidth = orient === 'vertical' ? width : height - (n - 1) * nodeGap;
    const ky = (nodeWidth * rootWidthPercent) / sum;
    if (ky < minKy) {
      minKy = ky;
    }
  });
  zrUtil.each(nodesByBreadth, function (nodes) {
    zrUtil.each(nodes, function (node, i) {
      const nodeDy = node.getLayout().value * minKy;
      if (orient === 'vertical') {
        node.setLayout({ x: i }, true);
        node.setLayout({ dx: nodeDy }, true);
      } else {
        node.setLayout({ y: i }, true);
        node.setLayout({ dy: nodeDy }, true);
      }
    });
  });

  zrUtil.each(edges, function (edge) {
    const edgeDy = +edge.getValue() * minKy;
    edge.setLayout({ dy: edgeDy }, true);
  });
}

/**
 * Resolve the collision of initialized depth (y-position)
 */
function resolveCollisions(
  nodesByBreadth: GraphNode[][],
  nodeGap: number,
  height: number,
  width: number,
  orient: LayoutOrient
) {
  const keyAttr = orient === 'vertical' ? 'x' : 'y';
  zrUtil.each(nodesByBreadth, function (nodes) {
    nodes.sort(function (a, b) {
      return a.getLayout()[keyAttr] - b.getLayout()[keyAttr];
    });
    let nodeX;
    let node;
    let dy;
    let y0 = 0;
    const n = nodes.length;
    const nodeDyAttr = orient === 'vertical' ? 'dx' : 'dy';
    for (let i = 0; i < n; i++) {
      node = nodes[i];
      dy = y0 - node.getLayout()[keyAttr];
      if (dy > 0) {
        nodeX = node.getLayout()[keyAttr] + dy;
        orient === 'vertical'
          ? node.setLayout({ x: nodeX }, true)
          : node.setLayout({ y: nodeX }, true);
      }
      y0 = node.getLayout()[keyAttr] + node.getLayout()[nodeDyAttr] + nodeGap;
    }
    const viewWidth = orient === 'vertical' ? width : height;
    // If the bottommost node goes outside the bounds, push it back up
    dy = y0 - nodeGap - viewWidth;
    if (dy > 0) {
      nodeX = node.getLayout()[keyAttr] - dy;
      orient === 'vertical'
        ? node.setLayout({ x: nodeX }, true)
        : node.setLayout({ y: nodeX }, true);

      y0 = nodeX;
      for (let i = n - 2; i >= 0; --i) {
        node = nodes[i];
        dy = node.getLayout()[keyAttr] + node.getLayout()[nodeDyAttr] + nodeGap - y0;
        if (dy > 0) {
          nodeX = node.getLayout()[keyAttr] - dy;
          orient === 'vertical'
            ? node.setLayout({ x: nodeX }, true)
            : node.setLayout({ y: nodeX }, true);
        }
        y0 = node.getLayout()[keyAttr];
      }
    }
  });
}

/**
 * Change the y-position of the nodes, except most the right side nodes
 * @param nodesByBreadth
 * @param alpha  parameter used to adjust the nodes y-position
 */
function relaxRightToLeft(nodesByBreadth: GraphNode[][], alpha: number, orient: LayoutOrient) {
  zrUtil.each(nodesByBreadth.slice().reverse(), function (nodes) {
    zrUtil.each(nodes, function (node) {
      if (node.outEdges.length) {
        let y = sum(node.outEdges, weightedTarget, orient) / sum(node.outEdges, getEdgeValue);

        if (isNaN(y)) {
          const len = node.outEdges.length;
          y = len ? sum(node.outEdges, centerTarget, orient) / len : 0;
        }

        if (orient === 'vertical') {
          const nodeX = node.getLayout().x + (y - center(node, orient)) * alpha;
          node.setLayout({ x: nodeX }, true);
        } else {
          const nodeY = node.getLayout().y + (y - center(node, orient)) * alpha;
          node.setLayout({ y: nodeY }, true);
        }
      }
    });
  });
}

function weightedTarget(edge: GraphEdge, orient: LayoutOrient) {
  return center(edge.node2, orient) * (edge.getValue() as number);
}
function centerTarget(edge: GraphEdge, orient: LayoutOrient) {
  return center(edge.node2, orient);
}

function weightedSource(edge: GraphEdge, orient: LayoutOrient) {
  return center(edge.node1, orient) * (edge.getValue() as number);
}
function centerSource(edge: GraphEdge, orient: LayoutOrient) {
  return center(edge.node1, orient);
}

function center(node: GraphNode, orient: LayoutOrient) {
  return orient === 'vertical'
    ? node.getLayout().x + node.getLayout().dx / 2
    : node.getLayout().y + node.getLayout().dy / 2;
}

function getEdgeValue(edge: GraphEdge) {
  return edge.getValue() as number;
}

function sum<T>(array: T[], cb: (item: T, orient?: LayoutOrient) => number, orient?: LayoutOrient) {
  let sum = 0;
  const len = array.length;
  let i = -1;

  while (++i < len) {
    const value = +cb(array[i], orient);
    if (!isNaN(value)) {
      sum += value;
    }
  }
  return sum;
}

/**
 * Change the y-position of the nodes, except most the left side nodes
 */
function relaxLeftToRight(nodesByBreadth: GraphNode[][], alpha: number, orient: LayoutOrient) {
  zrUtil.each(nodesByBreadth, function (nodes) {
    zrUtil.each(nodes, function (node) {
      if (node.inEdges.length) {
        let y = sum(node.inEdges, weightedSource, orient) / sum(node.inEdges, getEdgeValue);

        if (isNaN(y)) {
          const len = node.inEdges.length;
          y = len ? sum(node.inEdges, centerSource, orient) / len : 0;
        }

        if (orient === 'vertical') {
          const nodeX = node.getLayout().x + (y - center(node, orient)) * alpha;
          node.setLayout({ x: nodeX }, true);
        } else {
          const nodeY = node.getLayout().y + (y - center(node, orient)) * alpha;
          node.setLayout({ y: nodeY }, true);
        }
      }
    });
  });
}
/**
 * 同层节点连线调整
 * newNode 节点信息
 * edgeType 节点边类型
 * keyAttr 横竖版区分
 */
function sameLayerNodeEdgeAdjustment(newNode, edgeType, keyAttr) {
  let sourceToMove;
  let targetToMove;
  newNode[edgeType].forEach((item, index) => {
    if (item.node1.getLayout().depth === item.node2.getLayout().depth) {
      if (newNode.id === item.node1.id) {
        if (item.node1.getLayout()[keyAttr] - item.node2.getLayout()[keyAttr] < 0) {
          sourceToMove = item;
        } else {
          targetToMove = item;
        }
      }
      if (newNode.id === item.node2.id) {
        if (item.node1.getLayout()[keyAttr] - item.node2.getLayout()[keyAttr] > 0) {
          sourceToMove = item;
        } else {
          targetToMove = item;
        }
      }
    }
  });
  if (sourceToMove) {
    const index = newNode[edgeType].indexOf(sourceToMove);
    if (index !== -1) {
      newNode[edgeType].splice(index, 1);
    }
    newNode[edgeType].push(sourceToMove);
  }
  if (targetToMove) {
    const index = newNode[edgeType].indexOf(targetToMove);
    if (index !== -1) {
      newNode[edgeType].splice(index, 1);
    }
    newNode[edgeType].unshift(targetToMove);
  }
}
/**
 * Compute the depth(y-position) of each edge
 */
function computeEdgeDepths(nodes: GraphNode[], orient: LayoutOrient) {
  const keyAttr = orient === 'vertical' ? 'x' : 'y';
  zrUtil.each(nodes, function (node) {
    const newNode = handleNode(node);
    (newNode ? newNode.outEdges : node.outEdges).sort(function (a, b) {
      return a.node2.getLayout()[keyAttr] - b.node2.getLayout()[keyAttr];
    });
    (newNode ? newNode.inEdges : node.inEdges).sort(function (a, b) {
      return a.node1.getLayout()[keyAttr] - b.node1.getLayout()[keyAttr];
    });
  });
  zrUtil.each(nodes, function (node) {
    let newNode = handleNode(node);
    if (newNode && newNode.outEdges) {
      sameLayerNodeEdgeAdjustment(newNode, 'outEdges', keyAttr);
    }
    if (newNode && newNode.inEdges) {
      sameLayerNodeEdgeAdjustment(newNode, 'inEdges', keyAttr);
    }
    let sy = 0;
    let ty = 0;
    zrUtil.each(newNode ? newNode.outEdges : node.outEdges, function (edge) {
      if (node.id === edge.node2.id) {
        edge.setLayout({ ty: 0 }, true);
        edge.setLayout({ ty: sy }, true);
      } else {
        edge.setLayout({ sy: sy }, true);
      }
      sy += edge.getLayout().dy;
    });
    zrUtil.each(newNode ? newNode.inEdges : node.inEdges, function (edge) {
      if (node.id === edge.node1.id) {
        edge.setLayout({ sy: 0 }, true);
        edge.setLayout({ sy: ty }, true);
      } else {
        edge.setLayout({ ty: ty }, true);
      }
      ty += edge.getLayout().dy;
    });
  });
}
